import React, { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Type,
  Hash,
  Calendar,
  File,
  Heading,
  AlignLeft,
  Lock,
  CheckSquare,
  Circle,
  Send,
  Mail,
  MoveUp,
  MoveDown,
  Edit,
  Trash2,
  Plus,
  Code,
  X,
} from 'lucide-react';

const ItemTypes = {
  FORM_ELEMENT: 'form_element'
};

const DraggableFormElement = ({ element }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.FORM_ELEMENT,
    item: element,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }));

  return (
    <div
      ref={drag}
      className="form-element"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <span className="element-icon">{element.icon}</span>
      <span>{element.label}</span>
    </div>
  );
};

const DropZone = ({ onDrop, isLast }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.FORM_ELEMENT,
    drop: (item) => onDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  }));

  return (
    <div
      ref={drop}
      className={`element-drop-zone ${isOver ? 'element-drop-zone-active' : ''} ${isLast ? 'element-drop-zone-last' : ''}`}
    />
  );
};

const EditForm = ({ element, onSave, onCancel }) => {
  const [label, setLabel] = useState(element.label);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...element, label });
  };

  return (
    <>
      <div className="modal-overlay" onClick={onCancel} />
      <form className="element-edit-form" onSubmit={handleSubmit}>
        <h3>Edit Element</h3>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Element Label"
          autoFocus
        />
        <div className="element-edit-form-buttons">
          <button type="submit" className="save">Save</button>
          <button type="button" className="cancel" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </>
  );
};

const DroppedElement = ({ element, onDrop, isLast, onMove, onDelete, onEdit, index, totalElements }) => {
  const [value, setValue] = React.useState(element.defaultValue || '');
  const [checked, setChecked] = React.useState(false);

  const handleChange = (e) => {
    const newValue = e.target.type === 'checkbox' || e.target.type === 'radio' ? e.target.checked : e.target.value;
    if (e.target.type === 'checkbox' || e.target.type === 'radio') {
      setChecked(newValue);
    } else {
      setValue(newValue);
    }
  };

  const renderFormElement = () => {
    const baseProps = {
      placeholder: element.label,
      value: value,
      onChange: handleChange,
      name: element.id,
    };

    switch (element.id.split('_')[0]) {
      case 'input':
        return <input type="text" {...baseProps} />;
      case 'number':
        return <input type="number" {...baseProps} />;
      case 'date':
        return <input type="date" {...baseProps} />;
      case 'file':
        return <input type="file" onChange={handleChange} name={element.id} />;
      case 'heading':
        return <h2>{element.label}</h2>;
      case 'textarea':
        return <textarea {...baseProps}></textarea>;
      case 'password':
        return <input type="password" {...baseProps} />;
      case 'checkbox':
        return (
          <label>
            <input 
              type="checkbox" 
              checked={checked}
              onChange={handleChange}
              name={element.id}
            />
            {element.label}
          </label>
        );
      case 'radio':
        return (
          <label>
            <input 
              type="radio" 
              checked={checked}
              onChange={handleChange}
              name={element.id}
            />
            {element.label}
          </label>
        );
      case 'submit':
        return (
          <button 
            type="button" 
            onClick={() => console.log('Form submitted with values:', value)}
          >
            {element.label}
          </button>
        );
      case 'email':
        return <input type="email" {...baseProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="dropped-form-element">
        <div className="form-preview-element">
          {renderFormElement()}
        </div>
        <div className="element-controls">
          {index > 0 && (
            <button
              className="element-control-button"
              onClick={() => onMove(index, index - 1)}
              title="Move Up"
            >
              <MoveUp size={14} />
            </button>
          )}
          {index < totalElements - 1 && (
            <button
              className="element-control-button"
              onClick={() => onMove(index, index + 1)}
              title="Move Down"
            >
              <MoveDown size={14} />
            </button>
          )}
          <button
            className="element-control-button edit"
            onClick={() => onEdit(element)}
            title="Edit"
          >
            <Edit size={14} />
          </button>
          <button
            className="element-control-button delete"
            onClick={() => onDelete(index)}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <DropZone onDrop={onDrop} isLast={isLast} />
    </>
  );
};

const FormBuilder = () => {
  const [droppedElements, setDroppedElements] = useState([]);
  const [showCode, setShowCode] = useState(false);
  const [editingElement, setEditingElement] = useState(null);

  const FORM_ELEMENTS = [
    { id: 'input_1', type: 'input', label: 'Text Input', icon: <Type size={18} /> },
    { id: 'number_1', type: 'number', label: 'Number Input', icon: <Hash size={18} /> },
    { id: 'date_1', type: 'date', label: 'Date Input', icon: <Calendar size={18} /> },
    { id: 'file_1', type: 'file', label: 'File Upload', icon: <File size={18} /> },
    { id: 'heading_1', type: 'heading', label: 'Heading', icon: <Heading size={18} /> },
    { id: 'textarea_1', type: 'textarea', label: 'Text Area', icon: <AlignLeft size={18} /> },
    { id: 'password_1', type: 'password', label: 'Password Input', icon: <Lock size={18} /> },
    { id: 'checkbox_1', type: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={18} /> },
    { id: 'radio_1', type: 'radio', label: 'Radio Button', icon: <Circle size={18} /> },
    { id: 'submit_1', type: 'submit', label: 'Submit Button', icon: <Send size={18} /> },
    { id: 'email_1', type: 'email', label: 'Email Input', icon: <Mail size={18} /> },
  ];

  const handleDrop = (index) => (item) => {
    const newElement = { ...item, id: `${item.id}_${Date.now()}` };
    const newElements = [...droppedElements];
    newElements.splice(index, 0, newElement);
    setDroppedElements(newElements);
  };

  const handleMove = (fromIndex, toIndex) => {
    const newElements = [...droppedElements];
    const [movedElement] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, movedElement);
    setDroppedElements(newElements);
  };

  const handleDelete = (index) => {
    const newElements = [...droppedElements];
    newElements.splice(index, 1);
    setDroppedElements(newElements);
  };

  const handleEdit = (element) => {
    setEditingElement(element);
  };

  const handleSaveEdit = (editedElement) => {
    const index = droppedElements.findIndex(el => el.id === editedElement.id);
    if (index !== -1) {
      const newElements = [...droppedElements];
      newElements[index] = editedElement;
      setDroppedElements(newElements);
    }
    setEditingElement(null);
  };

  const handleCancelEdit = () => {
    setEditingElement(null);
  };

  const handleDeleteAll = () => {
    setDroppedElements([]);
  };

  const handleGenerateCode = () => {
    setShowCode(!showCode);
  };

  const generateFormCode = () => {
    return droppedElements.map(element => {
      switch (element.id.split('_')[0]) {
        case 'input':
          return `<input type="text" placeholder="${element.label}" />`;
        case 'number':
          return `<input type="number" placeholder="${element.label}" />`;
        case 'date':
          return `<input type="date" />`;
        case 'file':
          return `<input type="file" />`;
        case 'heading':
          return `<h2>${element.label}</h2>`;
        case 'textarea':
          return `<textarea placeholder="${element.label}"></textarea>`;
        case 'password':
          return `<input type="password" placeholder="${element.label}" />`;
        case 'checkbox':
          return `<label><input type="checkbox" /> ${element.label}</label>`;
        case 'radio':
          return `<label><input type="radio" /> ${element.label}</label>`;
        case 'submit':
          return `<button type="submit">${element.label}</button>`;
        case 'email':
          return `<input type="email" placeholder="${element.label}" />`;
        default:
          return '';
      }
    }).join('\n');
  };

  return (
    <div className="app">
      <nav className="navbar">
        <div className="nav-brand">FormBuilder</div>
        <div className="nav-links">
          <span>Features</span>
          <span>Docs</span>
          <span>Downloads</span>
          <span>🌙</span>
        </div>
      </nav>
      
      <div className="form-builder-container">
        <div className="drop-zone">
          {droppedElements.length === 0 ? (
            <DropZone onDrop={handleDrop(0)} isLast={true} />
          ) : showCode ? (
            <pre style={{ color: 'white', padding: '1rem' }}>
              {`<form>\n${generateFormCode()}\n</form>`}
            </pre>
          ) : (
            <div className="dropped-elements">
              {droppedElements.map((element, index) => (
                <DroppedElement
                  key={element.id}
                  element={element}
                  onDrop={handleDrop(index + 1)}
                  isLast={index === droppedElements.length - 1}
                  onMove={handleMove}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  index={index}
                  totalElements={droppedElements.length}
                />
              ))}
            </div>
          )}
        </div>
        
        <div className="sidebar">
          <div className="sidebar-elements">
            {FORM_ELEMENTS.map((element) => (
              <DraggableFormElement key={element.id} element={element} />
            ))}
          </div>
          <div className="sidebar-actions">
            <button className="action-button delete" onClick={handleDeleteAll}>
              <X size={16} /> Delete
            </button>
            <button className="action-button code" onClick={handleGenerateCode}>
              <Code size={16} /> {showCode ? 'Preview' : 'Code'}
            </button>
          </div>
        </div>
      </div>

      {editingElement && (
        <EditForm
          element={editingElement}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

export default FormBuilder;
